import React from 'react';
import BEM from '../../bem';
import tzTime from '../../utils/tzTime';
import { ReactComponent as CircledCheckMarckIcon } from '../../../common/images/circled-checkmark.svg';
import { ReactComponent as BellIcon } from '../../../common/images/bell.svg';
import { ReactComponent as CalendarIcon } from '../../../common/images/calendar.svg';
import { useAppDispatch, useAppSelector, actions } from 'redux-stores';
import { useCompleteTaskMutation, useReopenTaskMutation } from 'redux-stores/tasks';
import { PRIORITY_LABEL } from 'js-sdk/src/types/Tasks';

const classes = BEM.with('TaskView');

const TaskView: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedTask = useAppSelector((state) => state.tasks.selectedTask);
  const [completeTask, { isLoading: isCompleting }] = useCompleteTaskMutation();
  const [reopenTask, { isLoading: isReopening }] = useReopenTaskMutation();
  const organizationId = useAppSelector((state) => state.session.currentOrganizationId);

  if (!selectedTask || !organizationId) {
    return (
      <div className={classes('')} data-test-id="task-view">
        <div className={classes('empty')} data-test-id="task-view-empty">
          <CircledCheckMarckIcon /> Select a task to view details
        </div>
      </div>
    );
  }

  const isCompleted = selectedTask.status === 'completed';
  const isBusy = isCompleting || isReopening;
  const isOverdue =
    !isCompleted && selectedTask.due_at ? new Date(selectedTask.due_at) < new Date() : false;
  const dueLabel = selectedTask.due_at
    ? tzTime(selectedTask.due_at).format('MMM D, YYYY h:mm A')
    : null;

  const handleToggleComplete = async () => {
    if (isBusy) return;
    try {
      const newStatus = isCompleted ? 'open' : 'completed';
      if (isCompleted) {
        await reopenTask({ organizationId, taskId: selectedTask.id }).unwrap();
      } else {
        await completeTask({ organizationId, taskId: selectedTask.id }).unwrap();
      }
      dispatch(actions.updateSelectedTask({ status: newStatus }));
    } catch {
      console.error('Failed to toggle task completion');
    }
  };

  const handleClose = () => {
    dispatch(actions.setSelectedTask(null));
  };

  return (
    <div className={classes('')} data-test-id="task-view">
      <div className={classes('header')}>
        {selectedTask.priority && (
          <div className={classes('priority')}>
            <span className={classes('priority-pill', { priority: selectedTask.priority })}>
              {PRIORITY_LABEL[selectedTask.priority]}
            </span>
          </div>
        )}
        <div className={classes('status')}>
          {selectedTask.status === 'open' ? (
            <span className={classes('status-pill', { status: selectedTask.status })}>Open</span>
          ) : (
            <span className={classes('status-pill', { status: selectedTask.status })}>
              Completed
            </span>
          )}
        </div>
        <h2 className={classes('title')}>{selectedTask.title}</h2>
        <button
          className={classes('close-btn')}
          onClick={handleClose}
          type="button"
          aria-label="Close task view"
          data-test-id="task-view-close"
        >
          ✕
        </button>
      </div>

      <div className={classes('actions')}>
        <button
          className={classes('complete-btn', { completed: isCompleted })}
          onClick={handleToggleComplete}
          disabled={isBusy}
          type="button"
          data-test-id="task-view-complete-btn"
        >
          {isCompleted ? (
            <>
              <CircledCheckMarckIcon />
              Completed
            </>
          ) : (
            <>
              <CircledCheckMarckIcon />
              Mark Complete
            </>
          )}
        </button>
      </div>

      <div className={classes('section')}>
        <div className={classes('section-columns')}>
          {dueLabel && (
            <div>
              <h3 className={classes('section-title')}>Due Date</h3>
              <span className={classes('due-pill', { overdue: isOverdue })}>
                <CalendarIcon />
                {isOverdue ? 'Overdue • ' : ''}
                {dueLabel}
              </span>
            </div>
          )}
          <div>
            <h3 className={classes('section-title')}>Reminder</h3>
            <div className={classes('section--container')}>
              <BellIcon />
              {selectedTask.reminder_enabled ? 'On' : 'Off'}
            </div>
          </div>
        </div>
      </div>

      {selectedTask.description && (
        <div className={classes('section')}>
          <h3 className={classes('section-title')}>Description</h3>
          <p className={classes('description')}>{selectedTask.description}</p>
        </div>
      )}
    </div>
  );
};

export default TaskView;
